import {
  INotification,
  notificationEnums,
} from "@freetech/models/notifications";

const formatAmount = (amount: number) => `$${Math.abs(amount).toLocaleString()}`;

export const getAdminNotificationTitle = (
  notification: INotification
): string | undefined => {
  const { type, afterData, beforeData } = notification;

  switch (type) {
    case notificationEnums.AdminNotificationType.ADMIN_USER_CREATED:
      if (afterData && 'email' in afterData && 'role' in afterData) {
        return `New ${afterData.role} created: ${afterData.email}`;
      }
      return "New user created";

    case notificationEnums.AdminNotificationType.ADMIN_USER_SIGNED_UP:
      if (afterData && 'email' in afterData && 'role' in afterData) {
        return `New ${afterData.role} signup: ${afterData.email}`;
      }
      return "New user signed up";

    case notificationEnums.AdminNotificationType.ADMIN_USER_ONBOARDING_COMPLETE:
      if (afterData && 'email' in afterData && 'role' in afterData) {
        return `${afterData.role} onboarding complete: ${afterData.email}`;
      }
      return "User onboarding completed";

    case notificationEnums.AdminNotificationType.ADMIN_CLIENT_CREATED:
      if (afterData && 'name' in afterData) {
        return `New client: ${afterData.name}`;
      }
      return "New client created";

    case notificationEnums.AdminNotificationType.ADMIN_CLIENT_UPDATED:
      if (afterData && 'name' in afterData) {
        return `Client updated: ${afterData.name}`;
      }
      return "Client updated";

    case notificationEnums.AdminNotificationType.ADMIN_PROJECT_CREATED:
      if (afterData && 'name' in afterData && 'clientName' in afterData) {
        return `New project: ${afterData.name} (${afterData.clientName})`;
      }
      if (afterData && 'name' in afterData) {
        return `New project: ${afterData.name}`;
      }
      return "New project created";

    case notificationEnums.AdminNotificationType.ADMIN_PROJECT_UPDATED:
      if (afterData && 'name' in afterData) {
        return `Project updated: ${afterData.name}`;
      }
      return "Project updated";

    case notificationEnums.AdminNotificationType.ADMIN_CSA_CREATED:
      if (afterData && 'projectName' in afterData && 'clientName' in afterData) {
        return `New CSA: ${afterData.projectName} (${afterData.clientName})`;
      }
      return "New consulting services agreement created";

    case notificationEnums.AdminNotificationType.ADMIN_CSA_SIGNED_BY_ADMIN:
      if (afterData && 'projectName' in afterData) {
        return `CSA signed by admin: ${afterData.projectName}`;
      }
      return "CSA signed by admin";

    case notificationEnums.AdminNotificationType.ADMIN_CSA_SIGNED_BY_STAKEHOLDER:
      if (afterData && 'projectName' in afterData && 'stakeholderName' in afterData) {
        return `CSA signed by ${afterData.stakeholderName}: ${afterData.projectName}`;
      }
      return "CSA signed by stakeholder";

    case notificationEnums.AdminNotificationType.ADMIN_INVOICE_CREATED:
      if (afterData && 'totalDollarAmountDue' in afterData && 'clientName' in afterData) {
        return `New invoice: ${formatAmount(afterData.totalDollarAmountDue)} (${afterData.clientName})`;
      }
      if (afterData && 'totalDollarAmountDue' in afterData) {
        return `New invoice: ${formatAmount(afterData.totalDollarAmountDue)}`;
      }
      return "New invoice created";

    case notificationEnums.AdminNotificationType.ADMIN_INVOICE_PAID:
      if (afterData && 'totalDollarAmountDue' in afterData && 'clientName' in afterData) {
        return `Invoice paid: ${formatAmount(afterData.totalDollarAmountDue)} (${afterData.clientName})`;
      }
      return "Invoice has been paid";

    case notificationEnums.AdminNotificationType.ADMIN_PAYMENT_CREATED:
      if (afterData && 'amount' in afterData && 'recipientName' in afterData) {
        return `New payment: ${formatAmount(afterData.amount)} to ${afterData.recipientName}`;
      }
      if (afterData && 'amount' in afterData) {
        return `New payment: ${formatAmount(afterData.amount)}`;
      }
      return "New payment created";

    case notificationEnums.AdminNotificationType.ADMIN_PAYMENT_STATUS_CHANGED:
      if (afterData && beforeData && 'status' in afterData && 'status' in beforeData && 'amount' in afterData) {
        const recipientInfo = 'recipientName' in afterData ? ` to ${afterData.recipientName}` : '';
        return `Payment${recipientInfo}: ${beforeData.status} → ${afterData.status} (${formatAmount(afterData.amount)})`;
      }
      return "Payment status changed";

    case notificationEnums.AdminNotificationType.ADMIN_PAYMENT_COMPLETED:
      if (afterData && 'amount' in afterData && 'recipientName' in afterData) {
        return `Payment completed: ${formatAmount(afterData.amount)} to ${afterData.recipientName}`;
      }
      return "Payment completed";

    case notificationEnums.AdminNotificationType.ADMIN_PROJECT_ASSIGNMENT_CREATED:
      if (afterData && 'projectName' in afterData && 'freelancerName' in afterData) {
        return `New assignment: ${afterData.freelancerName} → ${afterData.projectName}`;
      }
      return "New project assignment created";

    case notificationEnums.AdminNotificationType.ADMIN_PROJECT_ASSIGNMENT_SIGNED:
      if (afterData && 'projectName' in afterData && 'freelancerName' in afterData) {
        return `Assignment signed: ${afterData.freelancerName} → ${afterData.projectName}`;
      }
      return "Project assignment signed";

    case notificationEnums.AdminNotificationType.ADMIN_TIMESHEET_CREATED:
      if (afterData && 'freelancerName' in afterData && 'hours' in afterData) {
        return `New timesheet: ${afterData.freelancerName} (${afterData.hours}h)`;
      }
      return "New timesheet created";

    case notificationEnums.AdminNotificationType.ADMIN_TIMESHEET_SUBMITTED:
      if (afterData && 'freelancerName' in afterData && 'hours' in afterData) {
        return `Timesheet submitted: ${afterData.freelancerName} (${afterData.hours}h)`;
      }
      return "Timesheet submitted for review";

    case notificationEnums.AdminNotificationType.ADMIN_TIMESHEET_APPROVED:
      if (afterData && 'freelancerName' in afterData && 'hours' in afterData) {
        return `Timesheet approved: ${afterData.freelancerName} (${afterData.hours}h)`;
      }
      return "Timesheet approved";

    case notificationEnums.AdminNotificationType.ADMIN_TIMESHEET_REJECTED:
      if (afterData && 'freelancerName' in afterData) {
        return `Timesheet rejected: ${afterData.freelancerName}`;
      }
      return "Timesheet rejected";
    default:
      return undefined;
  }
}; 